import { useState, useEffect, useId } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import TommyMascot, { TOMMY_STUDY_TIPS, TOMMY_ENCOURAGEMENTS } from './TommyMascot';
import type { TommyPose } from './TommyMascot';
import { ff } from '../styles/fonts';

const POSES_CYCLE: TommyPose[] = [
  'happy',
  'idea',
  'studying',
  'teaching',
  'cheerful',
  'excited',
  'proud',
  'focused',
  'thinking',
];

// Brand tokens (Learnex brand book). Kept local so this file never drifts to ad-hoc colors.
const TEAL = '#286958'; // Ocean Teal
const SNOW = '#FFF5F2'; // Snow White
const MUTED_AA = '#6B635B'; // muted text that passes 4.5:1 on white (the brief's #8A8178 does not at 11px)

interface TommyCompanionProps {
  currentContext?: 'study' | 'quiz' | 'dashboard' | 'general';
  defaultOpen?: boolean;
}

export default function TommyCompanion({
  currentContext = 'general',
  defaultOpen = false,
}: TommyCompanionProps) {
  const panelId = useId();

  // Always starts collapsed (no persisted open state). Persisting "open" across pages is what
  // made the card sit on top of Quick Reference / Subject Notes. Dashboard is always collapsed.
  const [isOpen, setIsOpen] = useState(currentContext === 'dashboard' ? false : defaultOpen);

  useEffect(() => {
    if (currentContext === 'dashboard') setIsOpen(false);
  }, [currentContext]);

  // Escape closes the card
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const [poseIndex, setPoseIndex] = useState(0);
  const [bubbleText, setBubbleText] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const currentPose = POSES_CYCLE[poseIndex];

  // Context-specific greeting when opening
  useEffect(() => {
    if (isOpen && !bubbleText) {
      if (currentContext === 'quiz') {
        setBubbleText("Take a breath! You've got this question.");
      } else if (currentContext === 'study') {
        setBubbleText("Focus mode activated. Let's master this topic!");
      } else {
        const randomGreeting =
          TOMMY_ENCOURAGEMENTS[Math.floor(Math.random() * TOMMY_ENCOURAGEMENTS.length)];
        setBubbleText(randomGreeting);
      }
    }
  }, [isOpen, currentContext]);

  function handlePoke() {
    setPoseIndex((prev) => (prev + 1) % POSES_CYCLE.length);
    const randomTip = TOMMY_STUDY_TIPS[Math.floor(Math.random() * TOMMY_STUDY_TIPS.length)];
    setBubbleText(randomTip);
  }

  function handlePokeKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePoke();
    }
  }

  const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2';

  return (
    // bottom-20 on small screens keeps Tommy above the mobile bottom tab bar (brief §9)
    <aside
      aria-label="Tommy Study Companion"
      className="fixed bottom-20 md:bottom-5 right-5 z-40 flex flex-col items-end"
    >
      {/* Expanded Buddy Card — solid light surface, light-only like the rest of the app.
          No dark: variants (the app has no dark theme, so they made the card dark on top of a light page),
          no backdrop blur / heavy shadow (brief §7). */}
      {isOpen ? (
        <div
          id={panelId}
          className="w-[min(20rem,calc(100vw-2.5rem))] bg-white rounded-2xl border border-outline-variant/30 shadow-[0_8px_24px_rgba(36,31,26,0.14)] overflow-hidden mb-3 animate-fade-in motion-reduce:animate-none"
        >
          {/* Card Header — flat Ocean Teal, no gradient */}
          <div
            className="px-4 py-3 text-white flex items-center justify-between"
            style={{ backgroundColor: TEAL }}
          >
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-white/90 motion-safe:animate-pulse" />
              <span className="font-semibold text-sm tracking-wide" style={ff}>
                Tommy • Your Study Buddy
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-1 text-white/80 hover:text-white rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                title={soundEnabled ? 'Mute sound effects' : 'Enable sound effects'}
                aria-label={soundEnabled ? 'Mute sound effects' : 'Enable sound effects'}
                aria-pressed={!soundEnabled}
              >
                <span className="material-symbols-outlined text-base">
                  {soundEnabled ? 'volume_up' : 'volume_off'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 text-white/80 hover:text-white rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                title="Minimize Tommy"
                aria-label="Minimize Tommy"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          </div>

          {/* Interactive Mascot Area */}
          <div className="p-4 flex flex-col items-center text-center">
            <div
              role="button"
              tabIndex={0}
              className={`py-2 px-3 cursor-pointer group rounded-xl ${focusRing}`}
              onClick={handlePoke}
              onKeyDown={handlePokeKey}
              aria-label="Tap Tommy for a study tip"
            >
              <TommyMascot
                pose={currentPose}
                size="lg"
                interactive
                soundEnabled={soundEnabled}
                withShadow
                speechBubble={null}
              />
              {/* Was "Tap Tommy to change pose (thinking)" — leaked internal pose names */}
              <span
                className="text-[11px] group-hover:text-primary transition-colors block mt-1 font-medium"
                style={{ color: MUTED_AA }}
              >
                Tap Tommy for a tip ✨
              </span>
            </div>

            {/* Speech Dialogue Bubble */}
            {bubbleText && (
              <div
                aria-live="polite"
                className="w-full p-3 rounded-2xl border border-outline-variant/30 text-sm text-on-surface text-left mt-2"
                style={{ backgroundColor: SNOW }}
              >
                <span className="font-semibold text-primary block mb-0.5 text-[11px] uppercase tracking-wider">
                  Tommy says:
                </span>
                <p className="leading-relaxed">{bubbleText}</p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 w-full mt-4">
              <button
                type="button"
                onClick={handlePoke}
                className={`flex items-center justify-center gap-1.5 py-2 px-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-xs font-medium transition-colors ${focusRing}`}
              >
                <span className="material-symbols-outlined text-sm">lightbulb</span>
                Next Study Tip
              </button>
              <Link
                to="/app/quiz/new"
                className={`flex items-center justify-center gap-1.5 py-2 px-3 bg-[#286958]/10 hover:bg-[#286958]/20 text-[#286958] rounded-xl text-xs font-medium transition-colors ${focusRing}`}
              >
                <span className="material-symbols-outlined text-sm">quiz</span>
                Quick Quiz
              </Link>
            </div>

            {/* Stuck on a problem? Labs handoff */}
            {currentContext === 'study' && (
              <Link
                to="/app/labs"
                className={`mt-2 w-full flex items-center justify-center gap-1.5 py-2 px-3 bg-surface-container border border-outline-variant/30 hover:border-primary/30 hover:bg-primary/5 text-on-surface-variant rounded-xl text-xs font-medium transition-colors ${focusRing}`}
              >
                <span className="material-symbols-outlined text-sm">science</span>
                Stuck on a problem? Try Labs →
              </Link>
            )}
          </div>
        </div>
      ) : null}

      {/* Floating Toggle Button — snow-white avatar so the tomato mascot pops
          (was orange-on-orange), static status dot (was a looping ping), brief hover lift of 2px */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={isOpen ? 'Close Tommy study buddy' : 'Open Tommy study buddy'}
        title={isOpen ? 'Collapse Tommy' : 'Chat with Tommy!'}
        className={`group relative flex items-center bg-white hover:bg-[#FFF5F2] text-on-surface rounded-full border border-outline-variant/40 shadow-[0_2px_12px_rgba(36,31,26,0.12)] transition hover:-translate-y-0.5 active:scale-95 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${focusRing} ${
          currentContext === 'dashboard' ? 'p-1.5' : 'p-1.5 pr-3 gap-2'
        }`}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden shrink-0 border border-[#FF6B4A]/30"
          style={{ backgroundColor: SNOW }}
        >
          <TommyMascot pose="happy" size="xs" withShadow={false} interactive={false} />
        </div>
        {currentContext !== 'dashboard' && (
          <span className="text-xs font-bold text-on-surface pr-1" style={ff}>
            Tommy
          </span>
        )}
        <span
          className="w-2 h-2 rounded-full absolute top-1 right-1 border border-white"
          style={{ backgroundColor: TEAL }}
        />
      </button>
    </aside>
  );
}